<template>
  <div>
    <div v-if="loading" class="loader">
      <!-- <b-spinner
        variant="primary"
        type="grow"
        :label="`${$t('reports.detractor.loadingLabel')}...`"
      ></b-spinner> -->
      <idx-skeleton-chart type="bar" />
    </div>
    <div v-else-if="emptyState">
      <div class="text-center">
        <p>
          <font-awesome-icon
            :icon="['fas', 'folder-open']"
            style="margin-right: 20px; font-size: 35px;"
          />
        </p>
        <h5>
          {{ this.$t('reports.detractor.noDataLabel') }}
        </h5>
        <p>
          {{ this.$t('reports.detractor.noDataMsg') }}
        </p>
      </div>
    </div>
    <div v-else class="px-3">
      <highcharts :options="chartOptions"></highcharts>
    </div>

    <div v-if="comparingAnswers.length > 0" class="row mt-4">
      <div class="col-12">
        <b-table
          striped
          id="last-answers-table"
          :fields="fields"
          :items="comparingAnswers"
          :busy="loadingTable"
          responsive
        >
          <template v-slot:table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner type="grow" class="align-middle"></b-spinner>
            </div>
          </template>
          <template v-slot:cell(name)="row">
            <div>
              {{ viewPermission(row.item.name) }}
            </div>
          </template>
          <template v-slot:cell(answerReview)="row">
            <div class="detractor-review">
              {{ row.item.answerReview }}
            </div>
          </template>
          <template v-slot:cell(previousAnswerReview)="row">
            <div class="detractor-review">
              {{ row.item.previousAnswerReview }}
            </div>
          </template>
          <template v-slot:cell(details)="row">
            <router-link
              v-if="userProfile.permissions.treatments.details"
              target="_blank"
              :to="`/treatments/${row.item.answerId}`"
              style="font-size: 20px;"
              :title="`${$t('reports.detractor.answerDetailsLabel')}`"
            >
              <font-awesome-icon :icon="['fas', 'search']" />
            </router-link>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import idxSkeletonChart from '@/components/molecules/idx-skeleton-chart';
import { format } from 'date-fns';
import api from '../../../../services/api';
import EventBus from '../../../../eventBus';

export default {
  name: 'DetractorReport',

  props: ['dataGraph'],

  components: {
    idxSkeletonChart,
  },

  data: () => ({
    loading: false,
    loadingTable: false,
    emptyState: false,
    userAuth: null,
    comparingAnswers: [],
    fields: [],
    chartOptions: {},
    isPanel: true,
  }),

  computed: {
    ...mapState('authentication', ['userProfile']),
    ...mapState('actions', ['myActionId', 'actionGroupData', 'myActionsIds']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('companies', ['myMetric']),
    ...mapState('branches', ['myIh1', 'myIh2', 'myHierarchies1', 'myHierarchies2']),
    ...mapState('indicators', ['indicatorName', 'indicatorData']),
  },

  watch: {
    actionGroupData: function watchActionGroup() {
      this.loadChart();
    },

    myActionId: function watchmyActionId() {
      this.loadChart();
    },

    myActionsIds: function watchmyActionsIds() {
      this.loadChart();
    },

    calendarDates: function watchCalendarDates() {
      this.loadChart();
    },

    myIh1: function watchMyIh1() {
      this.loadChart();
    },

    myHierarchies1: function watchMyHierarchies1(){
      this.loadChart();
    },

    myHierarchies2: function watchMyHierarchies2(){
      this.loadChart();
    },

    myIh2: function watchMyIh2() {
      this.loadChart();
    },

    myMetric: function watchMyMetric() {
      this.loadChart();
    },

    'indicatorData.value': function watchIndicatorDataValue() {
      this.loadChart();
    },
    'indicatorData.period': async function watchIndicatorPeriod() {
      await this.loadChart();
    },
  },

  methods: {
    viewPermission(name) {
      if (this.userProfile.permissions.answers.menu) {
        if (this.userProfile.permissions.answers.anonymous_profile) {
          return '****';
        }
        return name;
      } else if (this.userProfile.permissions.answers.anonymous_profile) {
        return '****';
      }
      return '****';
    },

    getMonthName(month) {
      // eslint-disable-next-line
      const monthNames = this.$i18n.messages[this.$i18n.locale].reports.fullMonthNames;
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < monthNames.length; i++) {
        if (monthNames[i].key.toLowerCase() === month.toLowerCase()) {
          return monthNames[i].value;
        }
      }
      return null;
    },

    async loadChart() {
      this.loading = true;
      this.emptyState = false;

      const response = await api.get('/reports/recurring-detractors', {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
          startDate: this.calendarDates.start,
          endDate: this.calendarDates.end,
          ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
          ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          metric: this.myMetric,
          groupId: this.actionGroupData._id,
          indicator: this.indicatorName,
          indicatorValue: this.indicatorData.value,
          indicatorRange: this.indicatorData.range,
        },
      });

      if (response.data.length === 0) {
        this.emptyState = true;
      }

      this.chartOptions.xAxis.categories = [];
      this.chartOptions.series[0].data = [];
      this.comparingAnswers = [];

      response.data.forEach((month) => {
        this.chartOptions.xAxis.categories.push(this.getMonthName(month.name));

        this.chartOptions.series[0].data.push({
          y: month.total,
          answers: month.answers,
        });
      });

      this.chartOptions.series[0].point.events.click = (e) =>
        this.handleClick(e.point.answers);

      this.loading = false;
    },

    async updateCardPanel(data) {
      this.loading = true;
      this.emptyState = false;

      const response = await api.get('/reports/recurring-detractors', {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          actionId: data.actionId ? data.actionId._id : null,
          startDate: '2022-01-01',
          endDate: '2022-07-07',
          ih1: data.ih1 ? data.ih1._id : null,
          // period: data.period ? data.period : null,
          metric: data.metric ? data.metric : null,
          indicatorValue: data.valueIndicator ? data.valueIndicator : null,
          indicator: data.indicator ? data.indicator : null,
          groupId: data.actionGroupId ? data.actionGroupId._id : null,
          // indicatorRange: this.indicatorData.range,
        },
      });

      if (response.data.length === 0) {
        this.emptyState = true;
      }

      this.chartOptions.xAxis.categories = [];
      this.chartOptions.series[0].data = [];
      this.comparingAnswers = [];

      this.chartOptions.chart.weight = this.dataGraph.layout.w * 30;
      this.chartOptions.chart.height = this.dataGraph.layout.h * 40;

      response.data.forEach((month) => {
        this.chartOptions.xAxis.categories.push(this.getMonthName(month.name));

        this.chartOptions.series[0].data.push({
          y: month.total,
          answers: month.answers,
        });
      });

      this.chartOptions.series[0].point.events.click = (e) =>
        this.handleClick(e.point.answers);

      this.loading = false;
    },

    async handleClick(answers) {
      this.loadingTable = true;

      const answerIds = [];

      answers.forEach((answer) => {
        answerIds.push(answer.answerId);
        answerIds.push(answer.previousAnswerId);
      });

      const response = await api.put(
        '/reports/recurring-detractors/last-answers',
        { answerIds },
        {
          headers: {
            'x-access-token': this.userAuth.token,
          },
        },
      );

      this.comparingAnswers = response.data;

      this.comparingAnswers.forEach((answer) => {
        answer.answerDate = format(
          new Date(answer.answerDate),
          'dd/MM/yyyy HH:mm',
        );

        answer.previousAnswerDate = format(
          new Date(answer.previousAnswerDate),
          'dd/MM/yyyy HH:mm',
        );
      });

      this.loadingTable = false;
    },
  },

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));
    this.fields = [
      { key: 'name', label: this.$t('reports.detractor.tableCustomerLabel') },
      { key: 'answerDate', label: this.$t('reports.detractor.tableDateLabel') },
      {
        key: 'answerReview',
        label: this.$t('reports.detractor.tableReviewLabel'),
      },
      {
        key: 'previousAnswerDate',
        label: this.$t('reports.detractor.tablePreviousDateLabel'),
      },
      {
        key: 'previousAnswerReview',
        label: this.$t('reports.detractor.tablePreviousReviewLabel'),
      },
      {
        key: 'details',
        label: 'Detalhes',
        class: this.$t('reports.detractor.tableDetailsLabel'),
      },
    ];
    this.chartOptions = {
      title: {
        text: this.isPanel ? '' : this.$t('reports.detractor.reportTitle'),
      },
      chart: {
        type: 'column',
      },
      xAxis: {
        categories: [],
        labels: {},
      },
      yAxis: {
        min: 0,
        gridLineWidth: 0,
        labels: {
          enabled: false,
        },
        title: {
          text: '',
        },
      },
      series: [
        {
          name: this.$t('reports.detractor.seriesNameLabel'),
          data: [],
          label: true,
          cursor: 'pointer',
          point: {
            events: {
              click: null,
            },
          },
        },
      ],
      colors: ['red'],
      plotOptions: {
        column: {
          dataLabels: {
            enabled: true,
          },
        },
      },
    };
  },

  async mounted() {
    if (this.$route.fullPath.indexOf('panels') !== -1) {
      this.isPanel = true;
      this.updateCardPanel(this.dataGraph);

      EventBus.$on('update-detractor-report', (items) => {
        this.updateCardPanel(items);
      });
    } else {
      await this.loadChart();
    }
  },

  beforeDestroy() {
    EventBus.$off('switch-chart');
    EventBus.$off('data-source');
    EventBus.$off('accumulated-report');
  },
};
</script>

<style>
.loader {
  width: 100%;
  height: 350px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.detractor-review {
  display: flex;
  justify-content: center;
  align-items: center;

  background: red;
  color: white;
  height: 30px;
  width: 30px;
  border-radius: 50%;
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/highcharts/detractor-report/index.vue